import { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Visibility, Delete } from '@mui/icons-material';
import { ColDef, ICellRendererParams, ValueFormatterParams } from 'ag-grid-community';
import { supabase } from '../../services/supabase';
import { useData } from '../../contexts/DataContext';
import DomainDetailsForm, { DomainFormValues } from '../domain/DomainDetailsForm';
import { useNotification } from '../../contexts/NotificationContext';
import { ConfirmationModal } from '../shared/ConfirmationModal';
import { useTranslation } from 'react-i18next';
import * as Sentry from '@sentry/react';
import DataTable from '../shared/DataTable';
import Modal from '../shared/Modal';
import Button from '../shared/Button';
import { formatDateTime } from '../../utils/dateUtils';
import { Domain } from '../../types/data';

// The rows come from the shared Domain, optionally joined with audit details.
type DomainRow = Domain;

const DomainsTab = () => {
  const { companyId } = useParams<{ companyId: string }>()!;
  const navigate = useNavigate();

  // Check if companyId is defined
  if (!companyId) {
    console.error('companyId is undefined');
    return <div>Error: Company ID is required.</div>; // Handle the error appropriately
  }

  const { domains, fetchDomains } = useData();
  const { t } = useTranslation();
  const { showNotification } = useNotification();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingDomain, setEditingDomain] = useState<DomainFormValues | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [domainToDelete, setDomainToDelete] = useState<string | null>(null);

  const handleSubmit = async (domain: DomainFormValues) => {
    try {
      // Prepare data for insertion, ensuring company_id is set
      const insertData = {
        domain: domain.domain,
        country: domain.country,
        language: domain.language,
        active: domain.active,
        display: domain.display,
        tone_of_voice: domain.tone_of_voice,
        company_id: companyId,
        modify_comment: domain.modify_comment,
      };

      const { error } = await supabase.from('domains').insert([insertData]);

      if (error) {
        showNotification(t('domain.error_adding_domain'), 'error');
        throw error;
      }

      showNotification(t('domain.domain_added_successfully'), 'success');

      setIsModalOpen(false);
      setEditingDomain(null);
      fetchDomains(companyId);
    } catch (error) {
      showNotification(t('domain.error_saving_domain'), 'error');
      Sentry.captureException(error);
      console.error('Error saving domain:', error);
    }
  };

  const handleDeleteClick = (id: string) => {
    setDomainToDelete(id);
    setIsDeleteModalOpen(true);
  };

  const handleDelete = async () => {
    if (!domainToDelete) return;

    try {
      const { error } = await supabase.from('domains').delete().eq('id', domainToDelete);

      if (error) throw error;
      showNotification(t('domain.domain_deleted_successfully'), 'success');
      fetchDomains(companyId);
    } catch (error) {
      console.error('Error deleting domain:', error);
      showNotification(t('domain.error_deleting_domain'), 'error');
      Sentry.captureException(error);
    } finally {
      setIsDeleteModalOpen(false);
      setDomainToDelete(null);
    }
  };

  const columnDefs: ColDef<Domain>[] = [
    {
      field: 'domain',
      headerName: t('domain.domain'),
      sortable: true,
      filter: true,
      onCellClicked: (params) => {
        navigate(`/company/${companyId}/${params.data?.id}`);
      },
      cellStyle: {
        cursor: 'pointer',
        color: '#3b82f6', // blue-500 color in Tailwind
      },
    },
    {
      field: 'country',
      headerName: t('domain.country'),
      sortable: true,
      filter: true,
    },
    {
      field: 'language',
      headerName: t('domain.language'),
      sortable: true,
      filter: true,
    },
    {
      field: 'modified_by_details',
      headerName: t('domain.modified_by'),
      sortable: true,
      filter: true,
      valueFormatter: (params: ValueFormatterParams<DomainRow>) => {
        const details = params.data?.modified_by_details;
        return details?.name || details?.email || '-';
      },
    },
    {
      field: 'updated_at',
      headerName: t('domain.last_updated'),
      sortable: true,
      filter: true,
      sort: 'desc',
      valueFormatter: (params: ValueFormatterParams<Domain>) => {
        return formatDateTime(params.value);
      },
    },
    {
      field: 'active',
      headerName: t('domain.status'),
      sortable: true,
      filter: true,
      cellRenderer: (params: ICellRendererParams<Domain>) => {
        return (
          <span
            className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
              params.value ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
            }`}
          >
            {params.value ? t('domain.active') : t('domain.inactive')}
          </span>
        );
      },
    },
    {
      headerName: t('domain.actions'),
      sortable: false,
      filter: false,
      cellRenderer: (params: ICellRendererParams<Domain>) => {
        return (
          <div className="flex space-x-2">
            <Link
              to={`/company/${companyId}/${params.data?.id}`}
              className="text-blue-600 hover:text-blue-900 mr-2"
            >
              <Visibility className="h-5 w-5" />
            </Link>
            <button
              onClick={() => handleDeleteClick(params.data?.id as string)}
              className="text-red-600 hover:text-red-900"
            >
              <Delete className="h-5 w-5" />
            </button>
          </div>
        );
      },
    },
  ];

  const gridConfig = {
    pagination: true,
    paginationAutoPageSize: true,
    defaultColDef: {
      flex: 1,
      minWidth: 100,
      resizable: true,
    },
  };

  return (
    <div className="flex flex-col h-full">
      <div className="flex justify-between items-center mb-4 flex-shrink-0">
        <h3 className="text-xl font-semibold">{t('domain.domains')}</h3>
        <Button onClick={() => setIsModalOpen(true)}>{t('domain.add_domain')}</Button>
      </div>

      {/* Domains List */}
      <div className="overflow-x-auto bg-white rounded-sm shadow flex-grow min-h-0">
        <DataTable<Domain>
          rowData={domains}
          columnDefs={columnDefs}
          gridConfig={gridConfig}
          isLoading={false}
          theme="ag-theme-alpine"
          height={'100%'}
          noRowsMessage={t('domain.no_domains')}
        />
      </div>

      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={editingDomain ? t('domain.edit_domain') : t('domain.add_domain')}
      >
        <DomainDetailsForm
          domain={editingDomain}
          companyId={companyId}
          onClose={() => setIsModalOpen(false)}
          onSubmit={handleSubmit}
          newDomain={true}
        />
      </Modal>

      <ConfirmationModal
        open={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleDelete}
        title={t('domain.delete_domain')}
        message={t('domain.delete_domain_message')}
      />
    </div>
  );
};

export default DomainsTab;
